import { useState, useRef, useEffect, useCallback } from 'react'

import { ModelCalculated } from './useCalculator'
import { createItemsToCompare, OfferFormData } from './helpers'

/**
 * Хук для отслеживания изменений в форме и элементах оборудования
 * Определяет, были ли внесены изменения для активации кнопки сохранения
 */

interface UseChangeTrackingProps {
  offerId?: string
  formValues: OfferFormData
  items: ModelCalculated[]
  personnelHouseholdServicesCost?: number
}

export const useChangeTracking = ({
  offerId,
  formValues,
  items,
  personnelHouseholdServicesCost,
}: UseChangeTrackingProps): {
  hasChanges: boolean
  setInitialValues: (
    formData: OfferFormData,
    itemsData: ModelCalculated[],
  ) => void
  resetChanges: () => void
  checkForChanges: (
    currentFormValues: OfferFormData,
    currentItems: ModelCalculated[],
  ) => void
} => {
  const [hasChanges, setHasChanges] = useState(false)
  const initialFormValues = useRef<OfferFormData | null>(null)
  const initialItems = useRef<ModelCalculated[]>([])

  // Функция для проверки изменений в форме или элементах
  const checkForChanges = useCallback(
    (
      currentFormValues: OfferFormData,
      currentItems: ModelCalculated[],
    ): void => {
      if (!offerId || !initialFormValues.current) {
        // Для новых предложений всегда разрешаем сохранение
        setHasChanges(true)
        return
      }

      // Сравнение значений формы
      const formChanged =
        JSON.stringify(currentFormValues) !==
        JSON.stringify(initialFormValues.current)

      // Сравнение элементов (исключая вычисляемые поля, которые могут изменяться при обновлении формы)
      const itemsToCompare = createItemsToCompare(currentItems)
      const initialItemsToCompare = createItemsToCompare(initialItems.current)

      const itemsChanged =
        JSON.stringify(itemsToCompare) !== JSON.stringify(initialItemsToCompare)

      setHasChanges(formChanged || itemsChanged)
    },
    [offerId],
  )

  // Отслеживание изменений формы
  useEffect(() => {
    if (initialFormValues.current) {
      const currentFormValues = {
        ...formValues,
        personnelHouseholdServicesCost:
          Number(personnelHouseholdServicesCost) || 0,
      }
      checkForChanges(currentFormValues, items)
    }
  }, [checkForChanges, formValues, items, personnelHouseholdServicesCost])

  // Установка начальных значений для сравнения
  const setInitialValues = (
    formData: OfferFormData,
    itemsData: ModelCalculated[],
  ): void => {
    initialFormValues.current = formData
    initialItems.current = itemsData
    setHasChanges(false)
  }

  // Сброс состояния изменений (для новых предложений)
  const resetChanges = (): void => {
    setHasChanges(false)
  }

  return {
    hasChanges,
    setInitialValues,
    resetChanges,
    checkForChanges,
  }
}
